import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

export type InfiniteScrollListProps<T> = {
  items: T[]
  renderItem: (item: T, index: number) => React.ReactNode
  keyExtractor: (item: T, index: number) => string | number
  onEndReached: () => void
  hasMore: boolean
  isLoading?: boolean // Optional: parent manages loading state, can be used by ListFooterComponent
  scrollableRootRef?: React.RefObject<HTMLElement>

  ListHeaderComponent?: React.ReactNode
  ListFooterComponent?: React.ReactNode // Parent can pass a loading spinner or end-of-list message here
  ListEmptyComponent?: React.ReactNode

  showDividers?: boolean
  onEndReachedThreshold?: number // Value between 0 and 1
  className?: string
}

const StyledContainer = styled.div`
  /* Default container styles can go here if necessary */
  overflow-y: auto;
  height: 100%;
  flex: 1;
`

const DefaultEmptyComponent = styled.div`
  padding: 20px;
  text-align: center;
  color: #888;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${({ theme }) => theme.Colors.Neutrals[50]};
`

/**
 * A controlled infinite scrolling list component.
 *
 * This component does not fetch data internally. Instead, it relies on the parent component
 * to provide the data (`items`), manage loading states (`isLoading`, `hasMore`), and handle
 * data fetching via the `onEndReached` callback.
 *
 * @param {InfiniteScrollListProps<T>} props - The props for the component.
 * @returns {React.ReactElement} The rendered infinite scroll list.
 *
 * @example
 * // Parent Component Usage:
 * const ParentComponent = () => {
 *   const [items, setItems] = useState<MyItemType[]>([]);
 *   const [hasMore, setHasMore] = useState(true);
 *   const [isLoading, setIsLoading] = useState(false);
 *   const [currentPage, setCurrentPage] = useState(0);
 *
 *   const fetchData = useCallback(async (pageToFetch: number) => {
 *     if (isLoading) return;
 *     setIsLoading(true);
 *     // const apiResponse = await yourApiService(pageToFetch, 20);
 *     // setItems(prev => pageToFetch === 0 ? apiResponse.data : [...prev, ...apiResponse.data]);
 *     // setHasMore(apiResponse.data.length > 0 && (pageToFetch * 20 + apiResponse.data.length) < apiResponse.total);
 *     // setCurrentPage(pageToFetch);
 *     setIsLoading(false);
 *   }, [isLoading]);
 *
 *   const handleEndReached = () => {
 *     if (hasMore && !isLoading) {
 *       fetchData(currentPage + 1);
 *     }
 *   };
 *
 *   useEffect(() => {
 *     fetchData(0); // Initial fetch
 *   }, []);
 *
 *   return (
 *     <InfiniteScrollList<MyItemType>
 *       items={items}
 *       renderItem={(item) => <MyItemComponent item={item} />}
 *       keyExtractor={(item) => item.id}
 *       onEndReached={handleEndReached}
 *       hasMore={hasMore}
 *       ListFooterComponent={isLoading ? <p>Loading...</p> : !hasMore ? <p>No more items.</p> : null}
 *       ListEmptyComponent={<p>No items to display.</p>}
 *     />
 *   );
 * };
 */
export function InfiniteScrollList<T>({
  items,
  renderItem,
  keyExtractor,
  onEndReached,
  hasMore,
  isLoading = false,
  ListHeaderComponent,
  ListFooterComponent,
  ListEmptyComponent = <DefaultEmptyComponent>No items found.</DefaultEmptyComponent>,
  showDividers = true,
  onEndReachedThreshold = 0.1,
  className,
  scrollableRootRef,
}: InfiniteScrollListProps<T>) {
  const observer = useRef<IntersectionObserver | null>(null)
  const onEndReachedCalled = useRef<boolean>(false)

  useEffect(() => {
    onEndReachedCalled.current = isLoading ? true : false
  }, [isLoading, items.length])

  const loadMoreTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isLoading && !onEndReachedCalled.current) {
            onEndReachedCalled.current = true
            onEndReached()
          }
        },
        {
          root: scrollableRootRef?.current || null,
          rootMargin: '0px',
          threshold: onEndReachedThreshold,
        },
      )

      if (node) observer.current.observe(node)
    },
    [hasMore, isLoading, onEndReached, onEndReachedThreshold, scrollableRootRef],
  )

  return items.length === 0 && !isLoading ? (
    <>{ListEmptyComponent}</>
  ) : (
    <StyledContainer className={className}>
      {ListHeaderComponent}
      {items.map((item, index) => (
        <React.Fragment key={keyExtractor(item, index)}>
          {renderItem(item, index)}
          {showDividers && index < items.length - 1 && <Divider />}
        </React.Fragment>
      ))}
      {ListFooterComponent}
      {hasMore && !ListFooterComponent && <div ref={loadMoreTriggerRef} style={{ height: '1px' }} />}
      {hasMore && ListFooterComponent && <div ref={loadMoreTriggerRef} style={{ height: '1px' }} />}
    </StyledContainer>
  )
}
